<template>
  <div>
    <v-container>
      <v-row>
        <h2 class="pb-2 pt-2">Verspätungs-Hotspots</h2>
      </v-row>
      <v-row>
        <v-col cols="12" class="pt-0">
          <small class="text-medium-emphasis">Datenbasis: Fernverkehr — ø auf dem Zulauf gewonnene Verspätung (min)</small>
        </v-col>
      </v-row>

      <v-row>
        <v-col cols="12" class="py-0">
          <AnonymousNotice />
        </v-col>
      </v-row>

      <template v-if="stations">
        <v-row>
          <v-col cols="12" md="6">
            <h3 class="pt-2 pb-2">Bahnhöfe (Klick für Segmente)</h3>
            <div style="height: 500px">
              <BarChart :datasets="stationChart.datasets" :labels="stationChart.labels"
                        :options="stationOptions"/>
            </div>
          </v-col>
          <v-col cols="12" md="6">
            <h3 class="pt-2 pb-2">{{ selectedStation ? `Einlaufende Segmente: ${selectedStation}` : 'Segmente' }}</h3>
            <div v-if="segments" style="height: 500px">
              <BarChart :datasets="segmentChart.datasets" :labels="segmentChart.labels"
                        :options="horizontalOptions"/>
            </div>
            <div v-else class="text-medium-emphasis">Einen Bahnhof links anklicken.</div>
          </v-col>
        </v-row>
      </template>
      <LoadingIndicator v-else/>
    </v-container>
  </div>
</template>

<script>
import { mapState } from 'vuex'
import AnonymousNotice from '@/components/AnonymousNotice.vue'
import BarChart from '@/components/BarChart.vue'
import LoadingIndicator from '@/components/LoadingIndicator.vue'
import SearchRangeMixin from '@/mixins/SearchRangeMixin'
import TitleMixin from '@/mixins/TitleMixin'

const DELTA_COLOR = '#c62828';

export default {
  name: 'RouteHotspots',

  components: { AnonymousNotice, BarChart, LoadingIndicator },

  mixins: [SearchRangeMixin, TitleMixin],

  data() {
    return {
      err: null,
      stations: null,
      segments: null,
      selectedStation: null,
      stationOptions: {
        maintainAspectRatio: false,
        indexAxis: 'y',
        plugins: { legend: { display: false } },
        scales: { x: { title: { display: true, text: 'Δ Verspätung/min' } } },
        onClick: (event, elements) => this.onStationClick(elements),
      },
      horizontalOptions: {
        maintainAspectRatio: false,
        indexAxis: 'y',
        plugins: { legend: { display: false } },
        scales: { x: { title: { display: true, text: 'Δ Verspätung/min' } } },
      },
    };
  },

  computed: {
    ...mapState({ searchRange: state => state.search.range }),

    title() {
      return `Verspätungs-Hotspots - ${import.meta.env.VITE_NAME}`;
    },

    stationChart() {
      return this.toDeltaChart(this.stations || [], s => s.station);
    },

    segmentChart() {
      return this.toDeltaChart((this.segments && this.segments.segments) || [], s => s.fromStation);
    },
  },

  methods: {
    toDeltaChart(rows, labelFn) {
      return {
        labels: rows.map(labelFn),
        datasets: [{
          label: 'Ø Delta (min)',
          backgroundColor: DELTA_COLOR,
          data: rows.map(row => Number(row.avgDelta.toFixed(2))),
        }],
      };
    },

    queryWithRange(path, extra) {
      const range = this.searchRangeToQuery(this.searchRange);
      const separator = range ? '&' : '?';
      const suffix = extra ? `${separator}${extra}` : '';
      return `${path}${range}${suffix}`;
    },

    load() {
      this.stations = null;
      this.segments = null;
      this.selectedStation = null;
      this.err = null;

      this.$api.get(this.queryWithRange('/route/hotspots'))
        .then(response => { this.stations = response.data.stations; })
        .catch(err => { this.err = err; this.$log.error(err); });
    },

    onStationClick(elements) {
      if (!elements || !elements.length || !this.stations) {
        return;
      }
      const station = this.stations[elements[0].index];
      if (!station) {
        return;
      }
      this.selectedStation = station.station;
      this.segments = null;
      this.$api.get(this.queryWithRange('/route/hotspots/segments', `station=${encodeURIComponent(station.evaStationId)}`))
        .then(response => { this.segments = response.data; })
        .catch(err => { this.err = err; this.$log.error(err); });
    },
  },

  mounted() {
    this.load();
  },

  watch: {
    searchRange() {
      this.load();
    },
  },
};
</script>
